import type { ComponentType, JSX } from 'react';
import { createElement, useEffect, useState } from 'react';
import { IconModule, ScreenLayout, clefDIcone } from '@taki/ui';
import { DOCK_ICONS, iconRegistrySnapshot, loadIconRegistry, resolveDockIcon } from './Dock.js';
import { portesDeFiche } from './acces.js';
import { actionsDuDock } from './raccourcis.js';
import type { ShellAppEntry, ShellAppRegistry } from './types.js';

/**
 * Registre de repli integre. En v0 le shell ne resout pas encore de vrais
 * bundles depuis `taki.manifest.json` ; a la place, pour chaque id declare
 * dans `config.installedApps` sans entree de registre explicite, on monte un
 * ecran placeholder (avec une icone neutre) pour que le routage, la liste
 * d'apps et l'auth guard soient exercables de bout en bout. Les vraies apps
 * surchargent les entrees via la prop `apps` de `TakiShell`.
 */

function humanizeId(id: string): string {
  const cleaned = id.replace(/[-_]+/g, ' ').trim();
  if (cleaned.length === 0) return id;
  return cleaned
    .split(' ')
    .map((word) => (word.length > 0 ? word[0]!.toUpperCase() + word.slice(1) : word))
    .join(' ');
}

function makePlaceholderComponent(id: string): ComponentType {
  // Meme un placeholder respecte le gabarit d'ecran standard (ScreenLayout,
  // cf. docs/GABARIT-ECRAN.md) : titre en zone 2, etat vide standard en zone 5.
  const Placeholder: ComponentType = () =>
    createElement(ScreenLayout, {
      title: humanizeId(id),
      empty: true,
      emptyTitle: 'Bientot disponible',
      emptyHint: `App "${id}" est installee mais n'a pas encore de bundle enregistre dans le shell.`,
    });
  Placeholder.displayName = `PlaceholderApp(${id})`;
  return Placeholder;
}

/**
 * L'ICONE D'UNE APP, DERIVEE DE SA FICHE, ET LE SEUL ENDROIT QUI LA DERIVE.
 *
 * `fiche.icon` est un NOM DE COMPOSANT (`IconCalendarMark`), alors que le
 * registre complet des icones est en clefs kebab et arrive en import DYNAMIQUE
 * (des centaines de kilo-octets, jamais dans le bundle). D'ou un composant et
 * non un noeud fige : a froid il rend `IconModule`, puis le vrai dessin des que
 * le registre est la, exactement comme les raccourcis du dock.
 *
 * La map curatee (`DOCK_ICONS`) passe AVANT : elle porte les alias historiques
 * (`IconContact` -> `contact-book`) et les glyphes de trait ecrits a la main
 * (`IconSparkle`), qu'aucune clef du registre ne nomme. Un nom que ni l'une ni
 * l'autre ne resout rend l'icone neutre ; `pnpm apps:check` le refuse d'abord.
 */
/* LA TAILLE EST CELLE QUE LE LECTEUR DEMANDE : le dock clone l'icone en 17, le
   hub en 15 (`cloneElement(entry.icon, { size })`). L'ignorer rendait toutes les
   icones du dock a 22, donc des rangees deformees (vu par le fondateur le 4/09). */
/**
 * `repli` EST LE NOM D'ICONE DE LA FICHE NATIVE, et il ne sert qu'a l'overlay
 * d'instance (`ficheEffective.ts`) : un client qui tape un nom d'icone que ni
 * la map curatee ni le registre ne connaissent garde l'icone de son app, il ne
 * se retrouve pas avec la pastille neutre. Sans `repli`, rien ne change : un
 * nom inconnu rend `IconModule`, comme avant.
 */
export function IconeDeFiche({
  nom,
  repli,
  size = 22,
}: {
  nom: string;
  repli?: string;
  size?: number;
}): JSX.Element {
  const [registre, setRegistre] = useState(iconRegistrySnapshot);
  const curatee = DOCK_ICONS[nom];

  useEffect(() => {
    if (curatee != null || registre !== null) return;
    let vivant = true;
    void loadIconRegistry().then((charge) => {
      if (vivant) setRegistre(charge);
    });
    return () => {
      vivant = false;
    };
  }, [curatee, registre]);

  /* LE NOM INCONNU NE SE VOIT QU'UNE FOIS LE REGISTRE LA : avant, on ne peut
     pas distinguer « nom faux » de « registre pas encore charge », et retomber
     tout de suite sur le repli ferait clignoter l'icone. */
  const inconnu = curatee == null && registre !== null && clefDIcone(nom, registre) === '';
  const choisi = inconnu && repli !== undefined ? repli : nom;
  const Icone = DOCK_ICONS[choisi] ?? resolveDockIcon(clefDIcone(choisi, registre), registre);
  return createElement(Icone, { size });
}

/**
 * Resout une {@link ShellAppEntry} concrete pour un id d'app installe, en
 * preferant une declaration fournie par l'appelant et en retombant sur un
 * placeholder genere (icone module neutre).
 *
 * LIBELLE, ICONE ET PORTES SE DERIVENT ICI, DE LA FICHE DE L'APP, et nulle part
 * ailleurs : ils vivaient aussi dans l'entree de registre, le Builder n'ecrivait
 * que la fiche, et le dock gardait l'ancien nom.
 *
 * LA FICHE N'EST PLUS JETEE. Elle l'etait (`const { fiche, ...reste }`), et
 * ses `entities` avec : le shell ne savait donc pas quelles DONNEES une app
 * touche, alors que c'est la seule chose que les rules regardent. Les PORTES en
 * sont derivees ici, une fois, et l'ecran des utilisateurs les lit.
 */
export function resolveAppEntry(
  id: string,
  overrides: ShellAppRegistry | undefined,
): ShellAppEntry {
  const declaration = overrides?.[id];
  if (declaration) {
    const { fiche, ...reste } = declaration;
    return {
      ...reste,
      /* LA FICHE NATIVE VOYAGE AVEC L'ENTREE depuis le studio client : c'est le
         MODELE de l'app (son nom, son icone, son libelle de menu, sa version),
         donc ce qu'un panneau de personnalisation compare a ce que l'org a
         ecrit, et ce qu'il recopie dans le cliche de la premiere instance. */
      fiche,
      label: fiche.menu.label,
      icon: createElement(IconeDeFiche, { nom: fiche.icon }),
      portes: portesDeFiche(fiche.entities),
      /* LES RACCOURCIS DU DOCK SE DERIVENT DES PORTES DECLAREES (`raccourcis.ts`),
         ceux ecrits a la main passent devant. Une app sans declaration (ecran
         ecrit a la main) garde ce que son entree dit, derivation comprise. */
      ...(reste.declaration === undefined
        ? {}
        : { actions: actionsDuDock(reste.actions, reste.declaration.ecrans, fiche.icon) }),
    };
  }

  // Un placeholder ne touche AUCUNE donnee : aucune porte, donc rien a
  // proposer dans l'ecran des utilisateurs. Ce n'est pas un manque, c'est
  // exact : une app sans bundle n'ecrit nulle part.
  const Component = makePlaceholderComponent(id);
  return {
    id,
    label: humanizeId(id),
    icon: createElement(IconModule, { size: 22 }),
    portes: [],
    load: async () => ({ default: Component }),
  };
}

/**
 * Construit la liste ordonnee des entrees d'apps, une par id. Le shell y
 * passe TOUT son registre (le routeur sait ouvrir chaque app du bundle) ; la
 * liste installee de l'org filtre a l'affichage (`installation.ts`).
 */
export function buildAppEntries(
  ids: readonly string[],
  overrides: ShellAppRegistry | undefined,
): ShellAppEntry[] {
  return ids.map((id) => resolveAppEntry(id, overrides));
}
